// Meeting chat over the call's own data channel: nothing is stored anywhere, it lasts only while the meeting runs.
// Someone joining late asks for the conversation so far; the person in the call longest sends it (in chunks that fit
// Daily's 4 KB message limit).
import { useCallback, useEffect, useRef, useState } from 'react';

export const MAX_CHAT = 1000;
const KEEP = 500;
const CHUNK_BYTES = 3000;
const bytes = (o) => new TextEncoder().encode(JSON.stringify(o)).length;
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const merge = (cur, more) => {
  const seen = new Set(cur.map((m) => m.id));
  const add = more.filter((m) => m && m.id && typeof m.text === 'string' && !seen.has(m.id));
  return add.length ? [...cur, ...add].sort((a, b) => a.at - b.at).slice(-KEEP) : cur;
};

function chunks(items) {
  const out = [];
  let cur = [];
  items.forEach((m) => {
    if (cur.length && bytes({ mm: 'chat-hist', items: [...cur, m] }) > CHUNK_BYTES) { out.push(cur); cur = []; }
    cur.push(m);
  });
  if (cur.length) out.push(cur);
  return out;
}

// The participant (other than the one asking) who has been in the call longest answers a history request.
function answerer(all, asker) {
  return Object.values(all).filter((p) => p?.session_id && p.session_id !== asker)
    .sort((a, b) => (new Date(a.joined_at || 0) - new Date(b.joined_at || 0)) || String(a.session_id).localeCompare(String(b.session_id)))[0];
}

export function useMeetingChat({ phase, local, send, onApp, participantsNow }, open) {
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const list = useRef(messages);
  const openRef = useRef(open);
  list.current = messages;
  openRef.current = open;

  useEffect(() => { if (open) setUnread(0); }, [open]);

  useEffect(() => onApp((ev) => {
    const m = ev?.data;
    if (!m?.mm) return;
    if (m.mm === 'chat' && m.msg) {
      setMessages((cur) => merge(cur, [m.msg]));
      if (!openRef.current) setUnread((n) => n + 1);
    } else if (m.mm === 'chat-hist' && Array.isArray(m.items)) {
      setMessages((cur) => merge(cur, m.items));
    } else if (m.mm === 'chat-req') {
      const all = participantsNow();
      if (list.current.length && answerer(all, ev.fromId)?.session_id === all.local?.session_id) {
        chunks(list.current).forEach((items) => send({ mm: 'chat-hist', items }, ev.fromId));
      }
    }
  }), [onApp, send, participantsNow]);

  useEffect(() => { if (phase === 'joined') send({ mm: 'chat-req' }); }, [phase, send]);

  const post = useCallback((raw) => {
    const text = String(raw || '').trim().slice(0, MAX_CHAT);
    if (!text || !local) return 'empty';
    const msg = { id: newId(), text, name: local.user_name || 'Guest', from: local.session_id, at: Date.now() };
    if (bytes({ mm: 'chat', msg }) > 3900) return 'long';
    if (!send({ mm: 'chat', msg })) return 'failed';
    setMessages((cur) => merge(cur, [msg]));
    return 'ok';
  }, [local, send]);

  return { messages, unread, post, me: local?.session_id };
}
